import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Building2,
  Shield,
  Settings,
  Search,
  ArrowLeft,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  BarChart3,
  MessageSquare,
  CalendarDays,
} from "lucide-react";
import Header from "../components/Header";
import LoadingLogo from "../components/LoadingLogo";
import { AuthContext } from "../contexts/AuthContext";
import {
  getAuthenticatedAxios,
  buildApiPath,
  API_ENDPOINTS,
} from "../utils/api";

function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/");
    }
  }, [user, navigate]);

  // Load system statistics
  const loadStats = async () => {
    try {
      setError(null);
      const axios = getAuthenticatedAxios();
      const response = await axios.get(buildApiPath(API_ENDPOINTS.ADMIN.STATS));
      setStats(response.data);
    } catch (err) {
      console.error("Failed to load stats:", err);
      // Only set error if it's not an auth error (which is handled by auth service)
      if (err.response?.status !== 401) {
        setError("Failed to load system statistics");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadStats();
    // Refresh stats every 60 seconds (increased from 30)
    const interval = setInterval(loadStats, 60000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    setRefreshing(true);
    loadStats();
  };

  // Navigation functions
  const navigateTo = (path) => {
    navigate(path);
  };

  // Format numbers with commas
  const formatNumber = (num) => {
    return num?.toLocaleString() || "0";
  };

  // Format currency
  const formatCurrency = (num) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num || 0);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 mt-1">System overview and management</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
            <button
              onClick={() => navigate("/")}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Search
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center h-64">
            <LoadingLogo size="md" message="Loading dashboard..." />
          </div>
        )}

        {/* Statistics Grid */}
        {!loading && stats && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                title="Total Users"
                value={formatNumber(stats.total_users)}
                change={stats.users_change_percent}
                icon={Users}
                color="blue"
              />
              <StatCard
                title="Total Companies"
                value={formatNumber(stats.total_companies)}
                change={stats.companies_change_percent}
                icon={Building2}
                color="green"
              />
              <StatCard
                title="Total Searches"
                value={formatNumber(stats.total_searches)}
                change={stats.searches_change_percent}
                icon={Search}
                color="purple"
              />
              <StatCard
                title="Total Cost (Month)"
                value={formatCurrency(stats.total_cost_month)}
                change={stats.cost_change_percent}
                icon={TrendingUp}
                color="orange"
              />
            </div>

            {/* Activity Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Today's Activity
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Active Users</span>
                    <span className="font-semibold">
                      {formatNumber(stats.active_users_today)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Searches</span>
                    <span className="font-semibold">
                      {formatNumber(stats.searches_today)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost</span>
                    <span className="font-semibold">
                      {formatCurrency(stats.total_cost_today)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  This Week
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Active Users</span>
                    <span className="font-semibold">
                      {formatNumber(stats.active_users_week)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Searches</span>
                    <span className="font-semibold">
                      {formatNumber(stats.searches_week)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost</span>
                    <span className="font-semibold">
                      {formatCurrency(stats.total_cost_week)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  This Month
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Active Users</span>
                    <span className="font-semibold">
                      {formatNumber(stats.active_users_month)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Searches</span>
                    <span className="font-semibold">
                      {formatNumber(stats.searches_month)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost</span>
                    <span className="font-semibold">
                      {formatCurrency(stats.total_cost_month)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Management Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <ActionCard
                title="User Management"
                description="View and manage all system users"
                icon={Users}
                onClick={() => navigateTo("/admin/users")}
                color="blue"
              />
              <ActionCard
                title="User Activity Timeline"
                description="View yearly heatmaps and search query history"
                icon={CalendarDays}
                onClick={() => navigateTo("/admin/user-activity")}
                color="teal"
              />
              <ActionCard
                title="Company Management"
                description="Manage companies and organizations"
                icon={Building2}
                onClick={() => navigateTo("/admin/companies")}
                color="green"
              />
              <ActionCard
                title="Usage Limits"
                description="Monitor and manage user search limits"
                icon={BarChart3}
                onClick={() => navigateTo("/admin/usage-limits")}
                color="indigo"
              />
              <ActionCard
                title="Survey Responses"
                description="View user feedback about unlimited plans"
                icon={MessageSquare}
                onClick={() => navigateTo("/admin/survey-responses")}
                color="pink"
              />
              <ActionCard
                title="Audit Logs"
                description="View system audit trail and security logs"
                icon={Shield}
                onClick={() => navigateTo("/admin/audit-logs")}
                color="purple"
              />
              <ActionCard
                title="Global Settings"
                description="Configure global prompts and system settings"
                icon={Settings}
                onClick={() => navigateTo("/admin/settings")}
                color="orange"
              />
            </div>

            {/* Top Lists */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top Companies */}
              {stats.top_companies && stats.top_companies.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Top Companies by Usage
                  </h3>
                  <div className="space-y-3">
                    {stats.top_companies.slice(0, 5).map((company, index) => (
                      <div
                        key={company.company_id}
                        className="flex justify-between items-center"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-500 w-6">
                            {index + 1}.
                          </span>
                          <div>
                            <p className="font-medium text-gray-900">
                              {company.company_name}
                            </p>
                            <p className="text-sm text-gray-600">
                              {company.user_count} users
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            {company.search_count}
                          </p>
                          <p className="text-xs text-gray-600">searches</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Users */}
              {stats.top_users && stats.top_users.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Top Users by Searches
                  </h3>
                  <div className="space-y-3">
                    {stats.top_users.slice(0, 5).map((user, index) => (
                      <div
                        key={user.user_id}
                        className="flex justify-between items-center"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-500 w-6">
                            {index + 1}.
                          </span>
                          <div>
                            <p className="font-medium text-gray-900">
                              {user.email}
                            </p>
                            <p className="text-sm text-gray-600">
                              {user.company_name}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            {user.search_count}
                          </p>
                          <p className="text-xs text-gray-600">searches</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, change, icon: Icon, color }) {
  const colorClasses = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
  };

  const isPositive = change > 0;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]} bg-opacity-10`}>
          <Icon
            className={`h-6 w-6 ${colorClasses[color].replace("bg-", "text-")}`}
          />
        </div>
        {change !== undefined && (
          <div
            className={`flex items-center gap-1 text-sm ${isPositive ? "text-green-600" : "text-red-600"}`}
          >
            {isPositive ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <TrendingDown className="h-4 w-4" />
            )}
            <span>{Math.abs(change).toFixed(1)}%</span>
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-600 mt-1">{title}</p>
    </div>
  );
}

// Action Card Component
function ActionCard({ title, description, icon: Icon, onClick, color }) {
  const colorClasses = {
    blue: "bg-blue-500 hover:bg-blue-600",
    green: "bg-green-500 hover:bg-green-600",
    purple: "bg-purple-500 hover:bg-purple-600",
    orange: "bg-orange-500 hover:bg-orange-600",
    indigo: "bg-indigo-500 hover:bg-indigo-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    teal: "bg-teal-500 hover:bg-teal-600",
  };

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow text-left group"
    >
      <div
        className={`p-3 rounded-lg ${colorClasses[color]} bg-opacity-10 inline-block mb-4 group-hover:bg-opacity-20 transition-colors`}
      >
        <Icon
          className={`h-6 w-6 ${colorClasses[color].split(" ")[0].replace("bg-", "text-")}`}
        />
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </button>
  );
}

export default AdminDashboard;
